import { useState, useMemo, useCallback, useRef } from "react";
import {
  useServices,
  useProfessionals,
  useWorkingHours,
  useAppointments,
  useCommitments,
  useCustomLunchs,
} from "../contexts";
import { Service } from "../@data";
import {
  generateAvailableDays,
  generateAllProfessionalsTimeSlots,
  generateAvailableTimeSlots,
  parseLocalDateString,
  getDaySchedule,
  buildEyebrowUpsellOffer,
  isEyebrowVariantService,
} from "../utils";

export const useSchedule = () => {
  const { services, categories, fetchServices } = useServices();
  const { professionals, fetchProfessionals } = useProfessionals();
  const { workingHours } = useWorkingHours();
  const { appointments, fetchAppointmentsByDate } = useAppointments();
  const {
    commitments,
    fixedCommitments,
    fetchCommitmentsByDate,
    fetchFixedCommitments,
  } = useCommitments();
  const { customLunchs, fetchCustomLunchsByDate } = useCustomLunchs();

  // Estados
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedService, setSelectedService] = useState<Service>();
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedProfessionalId, setSelectedProfessionalId] = useState("");
  const [noPreference, setNoPreference] = useState(false);

  // Refs
  const daysSectionRef = useRef<HTMLDivElement>(null);

  // Gera lista de dias disponíveis (hoje + 30 dias)
  const availableDays = useMemo(() => {
    return generateAvailableDays();
  }, []);

  // Filtra profissionais habilitados
  const enabledProfessionals = professionals.filter((prof) => prof.enabled);

  // Gera horários disponíveis para todos os profissionais no dia selecionado
  const availableTimeSlots = useMemo(() => {
    if (!selectedDay || !workingHours || !professionals.length) {
      return {};
    }

    return generateAllProfessionalsTimeSlots(
      professionals,
      selectedDay,
      workingHours,
      appointments,
      commitments,
      fixedCommitments,
      customLunchs,
      selectedService?.duration
    );
  }, [
    selectedDay,
    workingHours,
    professionals,
    appointments,
    commitments,
    fixedCommitments,
    customLunchs,
    selectedService?.duration,
  ]);

  // Verifica se um dia tem horários disponíveis
  const checkDayAvailability = useCallback(
    (date: string) => {
      if (!workingHours || !professionals.length) return false;

      // Verifica se o dia da semana está nos dias de funcionamento
      const selectedDate = parseLocalDateString(date);
      const dayOfWeek = selectedDate.getDay();

      const daySchedule = getDaySchedule(workingHours, dayOfWeek);

      if (!daySchedule || !daySchedule.active) {
        return false;
      }

      // Verifica se pelo menos um profissional tem horários disponíveis
      return enabledProfessionals.some((professional) => {
        const professionalSlots = generateAvailableTimeSlots(
          professional,
          date,
          workingHours,
          appointments,
          commitments,
          fixedCommitments,
          customLunchs
        );

        return professionalSlots.some((slot: any) => slot.available);
      });
    },
    [
      workingHours,
      professionals.length,
      enabledProfessionals,
      appointments,
      commitments,
      fixedCommitments,
      customLunchs,
    ]
  );

  // Mescla horários de todos os profissionais quando "Sem preferência" está ativo
  const mergedTimeSlots = useMemo(() => {
    if (!noPreference || !availableTimeSlots) return null;

    const allSlots = new Map<
      string,
      {
        time: string;
        available: boolean;
        professionals: string[];
        appointment?: any;
        commitment?: any;
        isLunch?: boolean;
      }
    >();

    // Coleta todos os horários únicos de todos os profissionais
    enabledProfessionals.forEach((professional) => {
      const professionalSlots = availableTimeSlots[professional.id] || [];
      professionalSlots.forEach((slot) => {
        if (!allSlots.has(slot.time)) {
          allSlots.set(slot.time, {
            time: slot.time,
            available: slot.available,
            professionals: [professional.id],
            appointment: slot.appointment,
            commitment: slot.commitment,
            isLunch: slot.isLunch,
          });
        } else {
          const existingSlot = allSlots.get(slot.time)!;
          existingSlot.professionals.push(professional.id);
          // Se pelo menos um profissional tem o horário disponível, considera disponível
          if (slot.available) {
            existingSlot.available = true;
          }
        }
      });
    });

    return Array.from(allSlots.values()).sort((a, b) =>
      a.time.localeCompare(b.time)
    );
  }, [noPreference, availableTimeSlots, enabledProfessionals]);

  // Filtra os serviços pela categoria e oculta variantes "/ SOBRANCELHA" (aparecem via oferta)
  const filteredServices = services
    .filter(
      (service) =>
        service.category === selectedCategory &&
        !isEyebrowVariantService(service.name)
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const getEyebrowUpsellOffer = useCallback(
    (service: Service = selectedService!) => {
      if (!service) return null;
      return buildEyebrowUpsellOffer(service, services);
    },
    [selectedService, services]
  );

  // Handlers
  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setSelectedService(undefined);
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);

    const firstAvailableDay = availableDays.find((day) =>
      checkDayAvailability(day.date)
    );
    if (firstAvailableDay) handleDaySelect(firstAvailableDay.date);

    setTimeout(() => {
      daysSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleDaySelect = (date: string) => {
    setSelectedDay(date);
    setSelectedTime("");
    setSelectedProfessionalId("");

    // Busca customLunchs para a data selecionada
    fetchCustomLunchsByDate(date);
    // Busca compromissos fixos (são globais, não dependem de data)
    fetchFixedCommitments();
  };

  const handleTimeSelect = (time: string, professionalId: string) => {
    setSelectedTime(time);
    setSelectedProfessionalId(professionalId);
  };

  const handleNoPreferenceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNoPreference(event.target.checked);
    setSelectedTime("");
    setSelectedProfessionalId("");
  };

  return {
    // Estados
    selectedCategory,
    selectedService,
    selectedDay,
    selectedTime,
    selectedProfessionalId,
    noPreference,

    // Dados
    services,
    categories,
    professionals: enabledProfessionals,
    workingHours,
    appointments,
    commitments,
    fixedCommitments,

    // Dados calculados
    availableDays,
    availableTimeSlots,
    mergedTimeSlots,
    filteredServices,
    getEyebrowUpsellOffer,

    // Funções
    fetchServices,
    fetchProfessionals,
    fetchAppointmentsByDate,
    fetchCommitmentsByDate,
    fetchFixedCommitments,
    fetchCustomLunchsByDate,
    checkDayAvailability,

    // Handlers
    handleCategorySelect,
    handleServiceSelect,
    handleDaySelect,
    handleTimeSelect,
    handleNoPreferenceChange,

    // Refs
    daysSectionRef,
  };
};
